import { Group, ActionIcon, Highlight } from "@mantine/core"
import { useInterval } from "@mantine/hooks"
import {
  IconRefresh,
  IconPlayerPause,
  IconPlayerPlay,
} from "@tabler/icons-react"
import { useNavigate } from "@tanstack/react-router"
import { MRT_ColumnDef } from "mantine-react-table"
import { DateTimeRangeSelector } from "../../../-components/infinit-scrolling-table/date-time-range-selector"
import {
  ExtendedInfinitScrollingTable,
  SearchProps,
} from "../../../-components/infinit-scrolling-table/extended-infinit-scrolling-table"
import { DateTime } from "../../../../../@client/System"
import { LogDto } from "../../../../../@client/Theasoft_ApiServer_Logs"
import { useInvalidate } from "../../../../../typed-client/hooks/use-invalidate"
import { getFormattedDate } from "../../../../../utils/get-formatted-date"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { LogLevel, LogLevelSelect } from "../../../-components/log-level"
import { useTranslation } from "react-i18next"
import { InfiniteUserSelect } from "@components/InfiniteUserSelect"
import classes from "./logging-table.module.css"

const route = "/api/Log/GetPaginatedLogData"

interface Props {
  searchProps: SearchProps<typeof route>
}

export function LoggingTable({ searchProps }: Props) {
  const { data: users } = useSuspenseQueryGet("/api/User/GetSimpleUsers", null)
  const { t } = useTranslation("app_server")
  const columns = React.useMemo<MRT_ColumnDef<LogDto>[]>(
    () => [
      { accessorKey: "id", header: "ID" },
      {
        accessorKey: "timeStamp",
        header: "",
        minSize: 125,
        size: 125,
        maxSize: 125,
        Cell: ({ cell }) =>
          getFormattedDate(cell.getValue<DateTime>(), "L LTS"),
        Filter: () => (
          <DateTimeRangeSelector
            variant="unstyled"
            value={{
              start: searchProps.value.start,
              end: searchProps.value.end,
            }}
            onConfirm={(value) =>
              searchProps.onChange({ ...searchProps.value, ...value })
            }
            placeholder={t("TimePeriod")}
            maxDate={new Date()}
          />
        ),
      },
      {
        accessorKey: "level",
        header: "",
        minSize: 55,
        size: 55,
        maxSize: 55,
        Filter: ({ column }) => (
          <LogLevelSelect
            variant="unstyled"
            value={column.getFilterValue() as LogLevel}
            onChange={(value) => column.setFilterValue(value)}
          />
        ),
        Cell: ({ cell }) => <LogLevel value={cell.getValue<LogLevel>()} />,
      },
      {
        accessorKey: "user.userName",
        header: "",
        minSize: 120,
        maxSize: 120,
        size: 120,
        Filter: () => (
          <InfiniteUserSelect
            styles={{ dropdown: { minWidth: 200 } }}
            placeholder="User"
            onChange={(v) =>
              searchProps.onChange({
                ...searchProps.value,
                userId: v != null ? parseInt(v) : null,
              })
            }
            value={searchProps.value.userId?.toString()}
            searchable
            clearable
            filterOptions={{ userTypeFilter: "Users" }}
            variant="unstyled"
          />
        ),
      },
      {
        accessorKey: "message",
        header: "",
        size: 1000,
        Cell: ({ row }) => (
          <Highlight size="xs" highlight={searchProps.value.globalFilter}>
            {row.original.message}
          </Highlight>
        ),
        Filter: () => null,
      },
      { accessorKey: "exception", header: "Exception" },
    ],
    [searchProps.value, users]
  )
  const invalidate = useInvalidate()
  const { start, stop, active } = useInterval(() => invalidate(route), 5 * 1000)
  const navigate = useNavigate()
  return (
    <ExtendedInfinitScrollingTable
      url={route}
      columnDefinitions={columns}
      defaultColumVisibility={{ id: false, exception: false, logEvent: false }}
      partialInput={{ ...searchProps.value }}
      searchProps={searchProps}
      defaultSorting={[{ id: "id", desc: true }]}
      forceTableState={true}
      tableState={{ showColumnFilters: true }}
      tableOptions={{
        mantineTableProps: { className: classes.testTd },
        enableSorting: false,
        enableColumnFilters: true,
        enableColumnActions: false,
        enableFilters: true,
        enableColumnResizing: false,
        enableColumnDragging: false,
        renderTopToolbarCustomActions: () => (
          <Group>
            <ActionIcon
              variant="transparent"
              c={"gray"}
              onClick={() => invalidate(route)}
            >
              <IconRefresh />
            </ActionIcon>
            {active ? (
              <ActionIcon variant="transparent" c={"gray"} onClick={stop}>
                <IconPlayerPause />
              </ActionIcon>
            ) : (
              <ActionIcon variant="transparent" c={"gray"} onClick={start}>
                <IconPlayerPlay />
              </ActionIcon>
            )}
          </Group>
        ),
      }}
      onClickRow={(log) =>
        navigate({
          to: "/app/admin-settings/logging/$id",
          params: { id: log.id.toString() },
        })
      }
    />
  )
}
